<script setup lang="ts">
import { $generateNodesFromSerializedNodes } from "@lexical/clipboard";
import { useElementBounding, useEventListener } from "@vueuse/core";
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $onUpdate,
  COMMAND_PRIORITY_EDITOR,
  type SerializedLexicalNode,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import type { CSSProperties } from "vue";
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from "vue";

import { backendOld } from "~/api";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import AiContentEditor from "~/components/text/AiContentEditor.vue";
import { MODEL_TO_DEFAULT_THINKING_LEVEL } from "~/constants/model";
import {
  AiModel,
  ChatMessageKind,
  FeedbackRating,
  type Language,
  RecommendationAction,
  RecommendationKind,
} from "~/shared/enums";
import type { Chat, DraftMessage, ExtraItem, UserTextMessage } from "~/shared/types";
import { useUserStore } from "~/stores";
import { getText, makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { when } from "~/utils/wait";

import {
  EVENT_CLEANUP_RECOMMENDATION_NODE,
  EVENT_OPEN_RECOMMENDATION_OVERLAY,
  LEXICAL_OUTSIDE_CLASS,
  RECOMMENDATION_BOX_CLASSES,
} from "../const";
import { AiCursorNode } from "../nodes/AiCursorNode";
import RecommendationButtons from "../nodes/RecommendationButtons.vue";
import RecommendationLoading from "../nodes/RecommendationLoading.vue";
import TextEditor from "../TextEditor.vue";
import { GENERIC_EDITOR_NODES, streamRecommendation, trimSerializedLexicalState, useStreamText } from "../utils";
import { removeMarkAndSelectEnd, removeMarkById, replaceMarkedSelection } from "../utils/markUtils";

const VERTICAL_GAP = 8;
const SPACER_EXTRA_HEIGHT = 300;

const props = defineProps<{
  duid: string;
  activeWrapperKey?: string | null;
  activeChildKey?: string | null;
  baseText?: boolean;
  toolbarTeleportKey: string;
}>();

const editor = useLexicalComposer();
const userStore = useUserStore();

const overlayEditorRef = ref<InstanceType<typeof TextEditor> | null>(null);
const editorRootElement = ref<HTMLElement | null>(null);
const teleportParentElement = ref<HTMLElement | null>(null);
const overlayRef = ref<HTMLElement | null>(null);

const isVisible = ref(false);
const content = ref("");
const isStreaming = ref(false);
const kind = ref<RecommendationKind | null>(null);
const anchorRect = ref<DOMRect | null>(null);
const markId = ref<string | undefined>();
const recommendationDuid = ref<string | undefined>();
const selectedMarkdown = ref<string | null>(null);
const customPromptInputRef = ref<InstanceType<typeof AiContentEditor> | null>(null);
const previousContext = ref("");

const now = new Date().toISOString();
const defaultModel = AiModel.AUTO;
const defaultThinkingLevel = MODEL_TO_DEFAULT_THINKING_LEVEL[defaultModel];
const customPromptChatDuid = makeDuid();
const customPromptDraft = ref<DraftMessage>({
  duid: makeDuid(),
  chatDuid: customPromptChatDuid,
  content: makeEmptyLexicalState(),
  kind: ChatMessageKind.DRAFT,
  authorDuid: userStore.duid,
  createdAt: now,
  attachmentDuids: [],
  extras: [],
});
const customPromptChat = ref<Chat>({
  duid: customPromptChatDuid,
  createdAt: now,
  updatedAt: now,
  title: "",
  userDuid: null,
  targetUserDuid: null,
  rootTaskDuid: null,
  sourceSkillDuid: null,
  sourceAgentScheduleDuid: null,
  extrasAreUserManaged: false,
  isDemo: false,
  pinned: false,
  read: false,
  accessibleByTeam: false,
  accessibleByUserDuids: [],
  working: false,
  model: defaultModel,
  thinkingLevel: defaultThinkingLevel,
  webEnabled: true,
});

const { streamText, stop } = useStreamText((text) => {
  content.value = previousContext.value + text;
});

const { top: rootTop, width: rootWidth, height: rootHeight } = useElementBounding(editorRootElement);
const { top: parentTop, width: parentWidth } = useElementBounding(teleportParentElement);

const hasContent = computed(() => !!content.value);
const isCustomKind = computed(() => kind.value === RecommendationKind.CUSTOM);

const positionStyles = computed<CSSProperties>(() => {
  if (!editorRootElement.value || !anchorRect.value || !teleportParentElement.value) {
    return {};
  }
  const containerRect = teleportParentElement.value.getBoundingClientRect();
  return {
    top: `${anchorRect.value.bottom - containerRect.top + VERTICAL_GAP}px`,
    width: "calc(100% + 14px)",
  };
});

const fillInRecommendation = async (
  recommendationKind: RecommendationKind,
  selectedText: string | null,
  language?: Language,
  customPromptValue?: string | null
) => {
  stop();

  recommendationDuid.value = undefined;
  isStreaming.value = true;

  const previousContent = content.value;
  const hasExistingContent = previousContent.trim().length > 0;
  previousContext.value = "";
  const previouslySuggestedContent = hasExistingContent ? previousContent : null;
  if (hasExistingContent) {
    content.value = "";
  }

  let previousText = "";

  await streamRecommendation({
    duid: props.duid,
    kind: recommendationKind,
    selectedText,
    language,
    onUpdate: (text, duid, end) => {
      if (text === "" && end) {
        stop();
        isStreaming.value = false;
        isVisible.value = false;
        return;
      }

      if (end) {
        recommendationDuid.value = duid;
        stop();
        content.value = previousContext.value + text;
        isStreaming.value = false;
      } else {
        streamText(text.substring(previousText.length));
        previousText = text;
      }
    },
    onError: () => {
      stop();
      isStreaming.value = false;
      isVisible.value = false;
    },
    customPrompt: customPromptValue ?? null,
    previouslySuggestedContent,
  });
};

const findTeleportParent = (element: HTMLElement | null): HTMLElement | null =>
  element?.closest<HTMLElement>(".group\\/text-editor") ?? element?.parentElement ?? document.body;

let spacerElement: HTMLElement | null = null;

const updateScrollBottom = () => {
  if (!overlayRef.value || !teleportParentElement.value) {
    spacerElement?.remove();
    spacerElement = null;
    return;
  }
  const overlayRect = overlayRef.value.getBoundingClientRect();
  const containerRect = teleportParentElement.value.getBoundingClientRect();
  const overflowPx = Math.max(0, overlayRect.bottom - containerRect.bottom);

  if (overflowPx === 0) {
    spacerElement?.remove();
    spacerElement = null;
    return;
  }

  if (!spacerElement) {
    spacerElement = document.createElement("div");
    spacerElement.style.width = "100%";
    teleportParentElement.value.insertAdjacentElement("afterend", spacerElement);
  }
  spacerElement.style.height = `${overflowPx + SPACER_EXTRA_HEIGHT}px`;
};

watch([() => content.value, () => isStreaming.value], async () => {
  if (!content.value) {
    return;
  }
  await nextTick();
  overlayEditorRef.value?.updateFromMarkdown(content.value, isStreaming.value);
  updateScrollBottom();
});

const hideOverlay = (options?: { skipMarkRemoval?: boolean }) => {
  const idToRemove = markId.value;
  stop();
  spacerElement?.remove();
  spacerElement = null;
  anchorRect.value = null;
  isVisible.value = false;
  content.value = "";
  recommendationDuid.value = undefined;
  markId.value = undefined;
  kind.value = null;
  isStreaming.value = false;
  if (idToRemove && !options?.skipMarkRemoval) {
    editor.update(() => removeMarkById(idToRemove), { discrete: true });
  }
};

const getOverlayRect = (): DOMRect | null => {
  const nativeSelection = window.getSelection();

  if (nativeSelection?.rangeCount) {
    const rect = nativeSelection.getRangeAt(0).getBoundingClientRect();
    if (rect.width > 0 || rect.height > 0) {
      return rect;
    }
  }

  if (!editorRootElement.value) {
    return null;
  }

  const { left, bottom, width } = editorRootElement.value.getBoundingClientRect();
  return new DOMRect(left, bottom, width, 0);
};

const focusCustomPromptInput = async () => {
  await when(() => !!customPromptInputRef.value?.isReady);
  customPromptInputRef.value?.focus();
};

const openOverlay = (payload: {
  markId: string | null;
  kind: RecommendationKind;
  language?: Language;
  selectedMarkdown: string | null;
}): boolean => {
  const allowNoSelection = payload.kind === RecommendationKind.CUSTOM;
  const rect = getOverlayRect();

  if (!rect || !editorRootElement.value) {
    return false;
  }

  markId.value = payload.markId || undefined;
  kind.value = payload.kind;
  anchorRect.value = rect;
  isVisible.value = true;
  selectedMarkdown.value = payload.selectedMarkdown;

  if (allowNoSelection) {
    nextTick(focusCustomPromptInput);
  } else {
    fillInRecommendation(payload.kind, payload.selectedMarkdown, payload.language);
  }
  nextTick(updateScrollBottom);

  return true;
};

const submitCustomPrompt = (message: UserTextMessage) => {
  const promptText = getText(message.content).trim();
  if (!promptText || !kind.value) {
    return;
  }
  fillInRecommendation(kind.value, selectedMarkdown.value, undefined, promptText);
  nextTick(() => {
    customPromptInputRef.value?.focus();
  });
};

const updateCustomPromptExtras = (extras: ExtraItem[]) => {
  customPromptDraft.value.extras = extras;
};

const insertSerializedNodes = (serializedNodes: SerializedLexicalNode[], mode?: "inline" | "below") => {
  if (serializedNodes.length === 0) {
    return;
  }
  const generatedNodes = $generateNodesFromSerializedNodes(serializedNodes);
  let selection = $getSelection();
  if (!selection) {
    const lastChild = $getRoot().getLastChild();
    if (lastChild) {
      lastChild.selectEnd();
      selection = $getSelection();
    }
  }
  if (!$isRangeSelection(selection)) {
    return;
  }
  if (mode === "below") {
    const block = selection.anchor.getNode().getTopLevelElementOrThrow();
    const blocks = generatedNodes.map((n) => ($isParagraphNode(n) ? n : $createParagraphNode().append(n)));
    blocks
      .reduce((anchor, node) => {
        anchor.insertAfter(node);
        return node;
      }, block)
      .selectEnd();
    return;
  }
  if (mode === "inline") {
    selection.insertRawText(" ");
    selection.insertNodes(generatedNodes.flatMap((n) => ($isParagraphNode(n) ? n.getChildren() : [n])));
    selection.insertRawText(" ");
    return;
  }
  selection.insertNodes(generatedNodes);
};

const getTrimmedOverlayNodes = (): SerializedLexicalNode[] => {
  const nodes = overlayEditorRef.value?.getSerializedNodes() ?? [];
  if (nodes.length === 0) {
    return nodes;
  }
  const emptyState = makeEmptyLexicalState();
  const trimmed = trimSerializedLexicalState({ ...emptyState, root: { ...emptyState.root, children: nodes } });
  return trimmed.root.children;
};

const handleAction = (actionType: RecommendationAction) => {
  editor.getRootElement()?.focus({ preventScroll: true });
  const currentMarkId = markId.value;
  const serializedNodes = getTrimmedOverlayNodes();

  if (recommendationDuid.value) {
    backendOld.recommendations.provideFeedback(
      recommendationDuid.value,
      actionType === RecommendationAction.DISCARD ? FeedbackRating.BAD : FeedbackRating.GOOD
    );
  }

  hideOverlay({ skipMarkRemoval: !!currentMarkId });

  editor.update(() => {
    if (actionType === RecommendationAction.REPLACE) {
      if (currentMarkId) {
        replaceMarkedSelection(currentMarkId);
        const selection = $getSelection();
        if ($isRangeSelection(selection) && serializedNodes.length > 0) {
          selection.insertNodes($generateNodesFromSerializedNodes(serializedNodes));
        } else if (serializedNodes.length > 0) {
          insertSerializedNodes(serializedNodes);
        }
      } else if (serializedNodes.length > 0) {
        const root = $getRoot();
        root.clear();
        root.append(...$generateNodesFromSerializedNodes(serializedNodes));
      }
    } else if (actionType === RecommendationAction.KEEP) {
      const shouldInsertBelow = currentMarkId ? removeMarkAndSelectEnd(currentMarkId) : false;
      insertSerializedNodes(serializedNodes, currentMarkId ? (shouldInsertBelow ? "below" : "inline") : undefined);
    } else if (actionType === RecommendationAction.DISCARD && currentMarkId) {
      removeMarkById(currentMarkId);
    }
  });

  editor.focus();
};

const onLoadingDiscard = () => handleAction(RecommendationAction.DISCARD);

const dialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null);
const discardConfirmed = ref(false);

const isActive = computed(() => isVisible.value || !!props.activeWrapperKey);

const closeOrConfirm = () => {
  if (isVisible.value && !hasContent.value) {
    editor.getRootElement()?.focus({ preventScroll: true });
    hideOverlay();
    editor.focus();
  } else {
    dialog.value?.openModal();
  }
};

const onConfirm = () => {
  if (isVisible.value) {
    handleAction(RecommendationAction.DISCARD);
    return;
  }
  if (props.activeChildKey) {
    editor.dispatchCommand(EVENT_CLEANUP_RECOMMENDATION_NODE, {
      key: props.activeChildKey,
      action: RecommendationAction.DISCARD,
      recommendationDuid: undefined,
    });
  }
};

const onDiscard = () => {
  if (isCustomKind.value) {
    nextTick(focusCustomPromptInput);
  }
};

useEventListener(document, "mousedown", (event: MouseEvent) => {
  if (!isActive.value || dialog.value?.isOpen) {
    return;
  }
  const target = event.target as Node;
  const wrapperEl = props.activeWrapperKey ? editor.getElementByKey(props.activeWrapperKey) : null;
  const isInLexicalOutside = (target as HTMLElement).closest?.(`.${LEXICAL_OUTSIDE_CLASS}`);
  if (overlayRef.value?.contains(target) || wrapperEl?.contains(target) || isInLexicalOutside) {
    return;
  }
  event.preventDefault();
  closeOrConfirm();
});

useEventListener(
  document,
  "keydown",
  (event: KeyboardEvent) => {
    if (!isActive.value || event.key !== "Escape" || dialog.value?.isOpen) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    closeOrConfirm();
  },
  { capture: true }
);

useEventListener(window, "pagehide", () => hideOverlay());

let unregisterListener: () => void;

onMounted(() => {
  editorRootElement.value = editor.getRootElement();
  teleportParentElement.value = findTeleportParent(editorRootElement.value);
  unregisterListener = editor.registerCommand(
    EVENT_OPEN_RECOMMENDATION_OVERLAY,
    (payload) => {
      // Measure after the command center's restored selection reaches the DOM.
      $onUpdate(() => openOverlay(payload));
      return true;
    },
    COMMAND_PRIORITY_EDITOR
  );
});

watch([rootTop, parentTop, rootWidth, parentWidth, rootHeight], async () => {
  if (!isVisible.value || !editorRootElement.value) {
    return;
  }
  await nextTick();
  const marks = editorRootElement.value.querySelectorAll("mark");
  if (marks.length) {
    const range = document.createRange();
    range.setStartBefore(marks[0]);
    range.setEndAfter(marks[marks.length - 1]);
    anchorRect.value = range.getBoundingClientRect();
  }
  nextTick(updateScrollBottom);
});

onBeforeUnmount(hideOverlay);

onUnmounted(() => {
  unregisterListener?.();
});
</script>

<template>
  <Teleport v-if="isVisible && anchorRect && editorRootElement && teleportParentElement" :to="teleportParentElement">
    <div
      ref="overlayRef"
      :class="RECOMMENDATION_BOX_CLASSES"
      :style="positionStyles"
      class="absolute ml-[-7px] px-[3px]"
      @mousedown.stop
      @click.stop>
      <div v-if="hasContent" @mousedown.stop @click.stop>
        <TextEditor
          ref="overlayEditorRef"
          namespace="recommendation-overlay"
          :nodes="[...GENERIC_EDITOR_NODES, AiCursorNode]"
          placeholder=""
          :left-margin-px="9"
          :right-margin-px="9"
          always-borderless
          :base-text="baseText" />
        <RecommendationButtons class="!pr-[9px]" @action="handleAction" />
      </div>
      <RecommendationLoading v-else-if="!isCustomKind" :kind="kind" @discard="onLoadingDiscard" />
      <div v-if="isCustomKind && hasContent" class="relative py-px">
        <hr class="absolute -inset-x-1 border-md" />
      </div>
      <div
        v-if="isCustomKind"
        class="-ml-1"
        :class="[hasContent && 'pt-2', customPromptInputRef?.isMultiline ? 'mb-[7px]' : 'mb-1.5']"
        @mousedown.stop
        @click.stop>
        <AiContentEditor
          ref="customPromptInputRef"
          :chat="customPromptChat"
          :message="customPromptDraft"
          is-draft
          :base-text="baseText"
          inline-buttons
          :is-send-secondary-button="hasContent"
          placeholder="Improve this text by..."
          editor-classes="!border-transparent focus:!border-transparent hover:!border-transparent !ring-0 focus:!ring-0"
          hide-attachment-button
          hide-model-switcher
          no-top-padding
          :toolbar-teleport-key="toolbarTeleportKey"
          @update-extras="updateCustomPromptExtras"
          @send="submitCustomPrompt" />
      </div>
    </div>
  </Teleport>
  <ConfirmationDialog
    ref="dialog"
    title="Discard recommendation?"
    description="You have unsaved AI-generated content. Are you sure you want to discard it?"
    confirm-text="Discard"
    cancel-text="Keep"
    focus-panel-initially
    @confirm="dialog?.isOpen ? (discardConfirmed = true) : onConfirm()"
    @cancel="discardConfirmed = false"
    @after-leave="discardConfirmed ? onConfirm() : onDiscard()" />
</template>
